import { TypedDocumentNode, gql } from '@apollo/client';

import { isType } from '@sorare/core/src/gql';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { SimpleReward, WithRewardRange } from '@sorare/core/src/lib/rewards';

import {
  RewardRange,
  RewardRangeVariant,
} from 'components/leaderboard/RewardRange';
import { ClickableRewardOverview } from 'components/rewards/ClickableRewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';

import { RankRewardTitle } from '../RankRewardTitle';
import { CumulativeRanking } from '../Ranking';
import {
  CumulativeRankReward_So5UserGroupMembership,
  CumulativeRankReward_so5RewardConfig,
} from './__generated__/index.graphql';

type Props = {
  so5RewardConfig: WithRewardRange<CumulativeRankReward_so5RewardConfig>;
  so5Ranking?: CumulativeRankReward_So5UserGroupMembership | null;
  simpleRewards: SimpleReward[];
  overview?: boolean;
  isMyRange: boolean;
  userGroupSlug: string;
  isMyNextRange: boolean;
  rewardOverviewVariant: Variant;
  rewardRangeVariant: RewardRangeVariant;
  activeLineupId?: string;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string
  ) => string;
};

export const CumulativeRankReward = ({
  so5RewardConfig,
  so5Ranking,
  simpleRewards,
  overview,
  isMyRange,
  isMyNextRange,
  rewardOverviewVariant,
  rewardRangeVariant,
  userGroupSlug,
  activeLineupId,
  withCollapsibleRanking = false,
  rankingLink,
}: Props) => {
  const isMyRangeOrMyNextRange = isMyRange || isMyNextRange;
  const shouldDisplay = !overview || isMyRangeOrMyNextRange;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <RewardRange
      key={so5RewardConfig.startRank}
      variant={rewardRangeVariant}
      title={<RankRewardTitle {...so5RewardConfig} />}
      rewards={simpleRewards.map((simpleReward, i) => {
        return (
          <ClickableRewardOverview
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            variant={rewardOverviewVariant}
            simpleReward={simpleReward}
            deliverableItems={so5RewardConfig.deliverableItems
              ?.map(item => item.deliverableItem)
              .filter(Boolean)}
            probabilisticBundleConfigs={
              so5RewardConfig.probabilisticBundleConfigs || []
            }
            experiences={so5RewardConfig.experiences || []}
          />
        );
      })}
    >
      {uniqueBy(
        ranking =>
          (
            (isType(ranking, 'So5UserGroupMembership') && ranking.ranking) ||
            0
          ).toString(),
        [
          so5RewardConfig.fromUserRanking,
          isMyRange && so5Ranking,
          so5RewardConfig.toUserRanking,
        ].filter(Boolean)
      ).map(
        ranking =>
          isType(ranking, 'So5UserGroupMembership') && (
            <CumulativeRanking
              key={ranking.ranking}
              so5Ranking={ranking}
              userGroupSlug={userGroupSlug}
              activeLineupId={activeLineupId}
              isMyRanking={ranking.ranking === so5Ranking?.ranking}
              withCollapsibleRanking={withCollapsibleRanking}
              rankingLink={rankingLink}
            />
          )
      )}
    </RewardRange>
  );
};

CumulativeRankReward.fragments = {
  so5RewardConfig: gql`
    fragment CumulativeRankReward_so5RewardConfig on So5RewardConfig {
      ranks
      rankPct
      fromUserRanking {
        id
        ... on So5UserGroupMembership {
          id
          ranking
          ...CumulativeRanking_So5UserGroupMembership
        }
      }
      toUserRanking {
        id
        ... on So5UserGroupMembership {
          id
          ranking
          ...CumulativeRanking_So5UserGroupMembership
        }
      }
      deliverableItems {
        deliverableItem {
          slug
          ...ClickableRewardOverview_deliverableItem
        }
      }
      probabilisticBundleConfigs {
        id
        ...ClickableRewardOverview_probabilisticBundleConfig
      }
      experiences {
        ...ClickableRewardOverview_so5RewardExperience
      }
    }
    ${ClickableRewardOverview.fragments.deliverableItem}
    ${ClickableRewardOverview.fragments.probabilisticBundleConfig}
    ${ClickableRewardOverview.fragments.so5RewardExperience}
    ${CumulativeRanking.fragments.So5UserGroupMembership}
  ` as TypedDocumentNode<CumulativeRankReward_so5RewardConfig>,
  So5UserGroupMembership: gql`
    fragment CumulativeRankReward_So5UserGroupMembership on So5UserGroupMembership {
      id
      ranking
      ...CumulativeRanking_So5UserGroupMembership
    }
    ${CumulativeRanking.fragments.So5UserGroupMembership}
  ` as TypedDocumentNode<CumulativeRankReward_So5UserGroupMembership>,
};
